
import { useSession } from "@supabase/auth-helpers-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SermonPlan, SermonFormData } from "./types";

export function useSermonPlansCrud() {
  const { toast } = useToast();
  const session = useSession();

  const createSermonPlan = async (sermonPlanData: Partial<SermonPlan> & SermonFormData) => {
    if (!session?.user) {
      toast({
        title: "Authentication Required",
        description: "You must be logged in to create sermon plans.",
        variant: "destructive",
      });
      return false;
    }
    
    try {
      // Make sure title is not optional for insert
      if (!sermonPlanData.title) {
        throw new Error("Title is required");
      }
      
      const { error } = await supabase.from("sermon_plans").insert({
        title: sermonPlanData.title,
        scripture_reference: sermonPlanData.scripture_reference || null,
        notes: sermonPlanData.notes || null,
        planned_date: sermonPlanData.planned_date || null,
        auto_generated: sermonPlanData.auto_generated || false,
        created_by: session.user.id,
        theme: sermonPlanData.theme || null,
        target_duration: sermonPlanData.target_duration || null,
        key_points: sermonPlanData.key_points || null,
        sermon_type: sermonPlanData.sermon_type || null
      });

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Sermon plan created successfully",
      });
      
      return true;
    } catch (error: any) {
      console.error("Error saving sermon plan:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save sermon plan",
        variant: "destructive",
      });
      return false;
    }
  };

  const updateSermonPlan = async (id: string, sermonPlanData: Partial<SermonPlan> & SermonFormData) => {
    try {
      const { error } = await supabase
        .from("sermon_plans")
        .update({
          title: sermonPlanData.title,
          scripture_reference: sermonPlanData.scripture_reference || null,
          notes: sermonPlanData.notes || null,
          planned_date: sermonPlanData.planned_date || null,
          theme: sermonPlanData.theme || null,
          target_duration: sermonPlanData.target_duration || null,
          key_points: sermonPlanData.key_points || null,
          sermon_type: sermonPlanData.sermon_type || null
        })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Sermon plan updated successfully",
      });
      
      return true;
    } catch (error: any) {
      console.error("Error updating sermon plan:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update sermon plan",
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteSermonPlan = async (id: string) => {
    try {
      const { error } = await supabase.from("sermon_plans").delete().eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Sermon plan deleted successfully",
      });
      return true;
    } catch (error: any) {
      console.error("Error deleting sermon plan:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete sermon plan",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    createSermonPlan,
    updateSermonPlan,
    deleteSermonPlan
  };
}
